/**
 * WB4.5+ — fail-loud feedback for unwired / misrouted kernel handler dispatch.
 */

import type { WorkbenchAction } from '@shared/business-workbench';
import type { WorkbenchExecutionResult } from '@shared/business-workbench/workbench-action-safety';
import { isWorkbenchExecutionPending } from '@shared/business-workbench/workbench-action-safety';
import { logger } from '@shared/logger';
import { showWorkbenchError } from './runtime/workbench-notifications';

export const WORKBENCH_KERNEL_DISPATCH_FAILED_EVENT =
  'inflomatrix:workbench-kernel-dispatch-failed';
export const WORKBENCH_KERNEL_DISPATCH_COMPLETED_EVENT =
  'inflomatrix:workbench-kernel-dispatch-completed';

export type KernelDispatchFailureReason = 'unwired' | 'misrouted_gated';

export interface WorkbenchKernelDispatchFailedDetail {
  actionId: string;
  handlerRef: string;
  reason: KernelDispatchFailureReason;
  userMessage: string;
  businessDomain?: string;
}

export interface WorkbenchKernelDispatchCompletedDetail {
  actionId: string;
  handlerRef: string;
  result: WorkbenchExecutionResult;
}

export function emitWorkbenchKernelDispatchFailed(
  detail: WorkbenchKernelDispatchFailedDetail
): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(
    new CustomEvent<WorkbenchKernelDispatchFailedDetail>(WORKBENCH_KERNEL_DISPATCH_FAILED_EVENT, {
      detail,
    })
  );
}

export function emitWorkbenchKernelDispatchCompleted(
  detail: WorkbenchKernelDispatchCompletedDetail
): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(
    new CustomEvent<WorkbenchKernelDispatchCompletedDetail>(
      WORKBENCH_KERNEL_DISPATCH_COMPLETED_EVENT,
      { detail }
    )
  );
}

export function reportWorkbenchKernelDispatchFailure(input: {
  action: Pick<WorkbenchAction, 'id' | 'handlerRef'>;
  reason: KernelDispatchFailureReason;
  businessDomain?: string;
  userMessage?: string;
}): void {
  const userMessage =
    input.userMessage ??
    (input.reason === 'misrouted_gated'
      ? '이 작업은 보호된 실행 경로가 필요합니다.'
      : '지원하지 않는 작업입니다.');

  logger.error('workbench_kernel_dispatch_failed', undefined, {
    actionId: input.action.id,
    handlerRef: input.action.handlerRef,
    reason: input.reason,
    businessDomain: input.businessDomain,
  });

  try {
    showWorkbenchError(userMessage);
  } catch (error) {
    logger.warn('workbench_kernel_dispatch_notification_unavailable', { error });
  }

  emitWorkbenchKernelDispatchFailed({
    actionId: input.action.id,
    handlerRef: input.action.handlerRef,
    reason: input.reason,
    userMessage,
    ...(input.businessDomain ? { businessDomain: input.businessDomain } : {}),
  });
}

export function applyWorkbenchKernelDispatchFeedback(
  result: WorkbenchExecutionResult,
  action: Pick<WorkbenchAction, 'id' | 'handlerRef' | 'label'>
): void {
  if (isWorkbenchExecutionPending(result)) {
    return;
  }
  if (result.handled && result.executionPhase === 'succeeded') {
    return;
  }
  if (result.handled) {
    return;
  }
  const title = result.userMessage?.title?.trim();
  if (!title) {
    return;
  }
  reportWorkbenchKernelDispatchFailure({
    action,
    reason: title.includes('보호된') ? 'misrouted_gated' : 'unwired',
    businessDomain: result.objectRef.businessDomain,
    userMessage: title,
  });
}

/** Async gated completion bridge — terminal feedback + completion event. */
export function notifyGatedKernelDispatchCompletion(
  result: WorkbenchExecutionResult,
  action: Pick<WorkbenchAction, 'id' | 'handlerRef' | 'label'>
): void {
  emitWorkbenchKernelDispatchCompleted({
    actionId: action.id,
    handlerRef: action.handlerRef,
    result,
  });

  if (result.executionPhase === 'failed' && !result.handled) {
    applyWorkbenchKernelDispatchFeedback(result, action);
    return;
  }

  if (result.executionPhase === 'failed' || result.executionPhase === 'cancelled') {
    const title = result.userMessage?.title?.trim();
    if (title && result.executionPhase === 'failed') {
      logger.error('workbench_gated_kernel_dispatch_failed', undefined, {
        actionId: action.id,
        handlerRef: action.handlerRef,
        title,
      });
      try {
        showWorkbenchError(title);
      } catch (error) {
        logger.warn('workbench_gated_kernel_dispatch_notification_unavailable', { error });
      }
    }
  }
}
